// src/Pages/GMS/ISR/UI-Model/components/Canvas/Canvas.tsx
import React, { useEffect, useState, useRef } from 'react';
import { AddContainerLine } from './AddContainer/AddContainerLine';
import { EmptyState } from './EmptyState/EmptyState';
import { useContainerLayout } from './hooks/useContainerLayout';
import { useEditor } from '../../contexts/EditorContext';
import { uiModelApiService } from '../../services/UIModelApiService';
import { useUIModelApi } from '../../contexts/UIModelApiContext';
import { ContainerAPI } from '../../types/api.types';
import { Container as ContainerType } from '../../types/canvas.types';
import { Container } from './Container/Container';
import { generateComponentId } from '../../utils/IdGenerator';
import { containerStore } from '../../utils/containerRegistryBridge';
import { extractContainerRows } from '../../utils/modelTransformers';
import {
  findContainer,
  findContainerPath,
  findContainerWithParent,
  updateContainersRecursively
} from './utils/containerSearchUtils';

const checkIfNestedContainer = (container: ContainerType | ContainerAPI): boolean => {
    if ('metadata' in container && container.metadata?.isNested) {
        return true;
    }
    
    if ('content' in container && !('metadata' in container)) {
        if (container.metadata?.isNested) {
            return true;
        }
    }
    
    return false;
};

const validateAndFixContainerRows = (containers: ContainerType[]): ContainerType[] => {
  const rowValidation: Record<number, { containers: ContainerType[], totalWidth: number }> = {};
  
  containers.forEach(container => {
    const row = container.metadata?.rowIndex ?? 0;
    if (!rowValidation[row]) {
      rowValidation[row] = { containers: [], totalWidth: 0 };
    }
    const width = parseFloat(container.configuration.width) || 100;
    rowValidation[row].containers.push(container);
    rowValidation[row].totalWidth += width;
  });
  
  Object.keys(rowValidation).forEach(rowKey => {
    const row = Number(rowKey);
    const { containers: rowContainers, totalWidth } = rowValidation[row];
    
    rowContainers.sort((a, b) => a.order - b.order);
    
    const has100WidthContainer = rowContainers.some(c => parseFloat(c.configuration.width) >= 100);
    
    if (totalWidth > 100.01 || (has100WidthContainer && rowContainers.length > 1)) {
      let currentRow = row;
      let currentRowWidth = 0;
      
      rowContainers.forEach(container => {
        const width = parseFloat(container.configuration.width) || 100;
        
        if ((currentRowWidth + width > 100.01) || 
            (width >= 100 && currentRowWidth > 0)) {
          
          if (currentRowWidth > 0) {
            currentRow++;
            currentRowWidth = 0;
          }
        }
        
        if (container.metadata && container.metadata.rowIndex !== currentRow) {
          container.metadata.rowIndex = currentRow;
        }
        
        currentRowWidth += width;
        
        if (width >= 100) {
          currentRow++;
          currentRowWidth = 0;
        }
      });
    }
  });
  
  containers.forEach(container => {
    if (container.content.nestedContainers && container.content.nestedContainers.length > 0) {
      container.content.nestedContainers = validateAndFixContainerRows(container.content.nestedContainers);
    }
  });
  
  return containers;
};

const convertAPIContainersToInternal = (apiContainers: ContainerAPI[], parentId?: string): ContainerType[] => {
  const containers: ContainerType[] = apiContainers.map(apiContainer => {
    const nestedContainers = apiContainer.content.nestedContainers
      ? convertAPIContainersToInternal(apiContainer.content.nestedContainers, apiContainer.id)
      : [];
    const isNested = apiContainer.metadata?.isNested ?? !!parentId;
    const rowIndex = isNested 
          ? (apiContainer.metadata?.rowIndex ?? apiContainer.rowIndex ?? 0)
          : (apiContainer.metadata?.rowIndex !== undefined 
              ? apiContainer.metadata.rowIndex 
              : apiContainer.rowIndex)
    return {
      id: apiContainer.id,
      name: apiContainer.name,
      order: apiContainer.order,
      configuration: apiContainer.configuration,
      metadata: {
        type: (isNested ? 'nested-container' : 'container') as 'nested-container' | 'container',
        isNested: isNested,
        parentContainerId: apiContainer.metadata?.parentContainerId || parentId,
        rowIndex:  rowIndex
      },
      content: {
        type: (apiContainer.content.submodel ? 'subModel' : 'widget') as 'widget' | 'subModel' | 'container',
        componentId: apiContainer.content.submodel?.id || 'empty',
        'widget-type': 'contentBlock' as any,
        nestedContainers: nestedContainers,
        submodel: apiContainer.content.submodel,
        items: apiContainer.content.items || []
      },
      usageInfo: apiContainer.usageInfo
    } as ContainerType;
  });
  
  return validateAndFixContainerRows(containers);
};

type CanvasProps = {
  onContainerSelect?: (containerId: string) => void;
  onFieldSelect?: (fieldId: string, fieldType: string, containerId: string, parentId?: string) => void;
  onSubmodelSelect?: (submodelId: string, containerId?: string) => void;
  onWidgetSelect?: (widgetId: string, widgetType: string, configuration?: any, containerId?: string) => void;
  configureId: string;
  onContainerRowsChange?: (pageId: string, containerRows: Record<string, number>) => void;
}

export const Canvas = ({ onContainerSelect, onFieldSelect, onSubmodelSelect, onWidgetSelect, configureId, onContainerRowsChange } : CanvasProps) => {
  const { editorState, setEditorState, containers, setContainers, removeComponentFromContainer, deleteContainer, updateWidgetContent, selectContainer } = useEditor();
  const { canvasMap, canvas, setCanvas, currentUIModelId, markPageAsModified } = useUIModelApi();
  const { activePageId, selectedContainerId } = editorState;
  const [activeContainers, setActiveContainers] = useState<ContainerType[]>([]);
  const initialContainerRows = extractContainerRows(activeContainers);
  const updateContainersRef = useRef(false);
  const initializedRef = useRef<{[pageId: string]: boolean}>({});
  const containerRowsRef = useRef<Record<string, number>>({}); 
  const loadingRef = useRef<Set<string>>(new Set());
  const loadedCanvasRef = useRef<Set<string>>(new Set());

  const {
    containers: layoutContainers,
    setContainers: setLayoutContainers,
    rows,
    containerRows,
    setContainerRows,
    handleContainerClick,
    handleAddHorizontalContainer,
    handleAddVerticalContainer,
    handleAddNestedHorizontalContainer,
    handleAddNestedVerticalContainer,
    handleAddNestedBetween,
    setSelectedContainerId
  } = useContainerLayout(activeContainers, initialContainerRows);
  
  useEffect(() => {
    if (activePageId && onContainerRowsChange) {        
      containerRowsRef.current = containerRows;
      onContainerRowsChange(activePageId, containerRows);
    }
  }, [containerRows, activePageId]);

  useEffect(() => {
    if (activePageId && containers[activePageId]) {
      setActiveContainers(containers[activePageId]);
      initializedRef.current[activePageId] = true;
    } else {
      setActiveContainers([]);
    }
  }, [activePageId, activePageId ? containers[activePageId] : undefined]);

  useEffect(() => {
    const loadCanvasForPage = async () => {
      if (!activePageId || !currentUIModelId) return;
        const canvasId = canvasMap[activePageId];
        if (!canvasId) {
          console.log(`Canvas mapping not yet available for page ${activePageId}`);
          return;
        }
        
        const loadKey = `${activePageId}-${canvasId}`;
        if (loadedCanvasRef.current.has(loadKey)) {
          return;
        }
        
        if (loadingRef.current.has(loadKey)) {
          return;
        }
        
        if (canvas && canvas[canvasId] && canvas[canvasId].containers) {
          const canvasData = canvas[canvasId];
          const convertedContainers = convertAPIContainersToInternal(canvasData.containers);
          
          if (containers[activePageId] && containers[activePageId].length > 0) {
            loadedCanvasRef.current.add(loadKey);
            return;
          }
          
          setContainers(prev => ({
            ...prev,
            [activePageId]: convertedContainers
          }));
          loadedCanvasRef.current.add(loadKey);
          return;
        }
        
        if (!containers[activePageId] || containers[activePageId].length === 0) {
          loadingRef.current.add(loadKey);
          
          try {
            const response = await uiModelApiService.getCanvas(
              configureId,
              currentUIModelId,
              canvasId
            );
            
            if (response.success && response.data.canvas) {
              
              setCanvas(prev => ({
                ...prev,
                [canvasId]: response.data.canvas
              }));
              
              if (response.data.canvas.containers) {
                const convertedContainers = convertAPIContainersToInternal(response.data.canvas.containers);
                
                setContainers(prev => ({
                  ...prev,
                  [activePageId]: convertedContainers
                }));
              }
              
              loadedCanvasRef.current.add(loadKey);
            }
          } catch (error) {
            console.error('Failed to fetch canvas:', error);
          } finally {
            loadingRef.current.delete(loadKey);
          }
      }
    };
    loadCanvasForPage();
  }, [activePageId, currentUIModelId, canvasMap, canvas, configureId, containers]);

  useEffect(() => {
    loadedCanvasRef.current.clear();
    loadingRef.current.clear();
  }, [currentUIModelId]);

  useEffect(() => {
    if (activeContainers && activeContainers.length > 0) {
      containerStore.registerContainers(activeContainers);
    }
  }, [activeContainers]);

  useEffect(() => {
    if (activePageId && activeContainers.length > 0 && updateContainersRef.current) {
      setContainers(prev => ({
        ...prev,
        [activePageId]: activeContainers
      }));
    }
    
    updateContainersRef.current = false;
  }, [activeContainers, activePageId, setContainers]);

  const handleFieldSelect = (fieldId: string, fieldType: string, containerId: string, parentId?: string) => {
      if (onFieldSelect) {
          onFieldSelect(fieldId, fieldType, containerId, parentId);
      }
  };

  const handleContainerSelect = (containerId: string) => {
    const container = activePageId && containers[activePageId]
      ? findContainer(containers[activePageId], containerId)
      : null;
    
    const isNested = container ? checkIfNestedContainer(container) : false;
    const path: string[] = [];

    if (isNested) {
      if (activePageId && containers[activePageId]) {
        const containerPath = findContainerPath(containers[activePageId], containerId);
        if (containerPath) {
          path.push(...containerPath);
        }
      }
    } else {
      path.push(containerId);
      handleContainerClick(containerId);
    }
    
    selectContainer(containerId, path);
    
    if (onContainerSelect) {
      onContainerSelect(containerId);
    }
  };

  const handleRemoveComponent = (componentId: string, containerId: string) => {
    removeComponentFromContainer(componentId, containerId);
  };

  const updateActiveContainers = (updatedContainers: ContainerType[]) => {
      if (!activePageId) {
        return;
      }
      updateContainersRef.current = true;

      const containersWithRowMetadata = updatedContainers.map(container => {
        if (!container.metadata?.isNested && containerRowsRef.current[container.id] !== undefined) {
          return {
            ...container,
            metadata: {
              type: container.metadata?.type || 'container' as 'container' | 'nested-container',
              isNested: container.metadata?.isNested || false,
              parentContainerId: container.metadata?.parentContainerId,
              rowIndex: containerRowsRef.current[container.id]
            }
          };
        }
        return container;
      });

      setActiveContainers(containersWithRowMetadata);
      setContainers(prev => ({
        ...prev,
        [activePageId]: containersWithRowMetadata
      }));

      containerStore.registerContainers(containersWithRowMetadata);
  };

  const handleAddContainer = (afterContainerId: string) => {
        if (!editorState.activePageId) return;
        
        // Always add as a child of the container where "Add Container" was clicked
        // This creates Container 1.1.1 when clicking on Container 1.1
        const updatedContainers = handleAddNestedHorizontalContainer(afterContainerId);
        
        updateActiveContainers(updatedContainers);
        markPageAsModified(editorState.activePageId);
  };

  const handleContentUpdate = (widgetId: string, content: string) => {
      if (!activePageId) return;
      
      if (updateWidgetContent) {
          updateWidgetContent(widgetId, content);
      }
      
      markPageAsModified(activePageId);
  };

  const handleDuplicateContainer = (containerId: string) => {
        if (!editorState.activePageId) return;

        const cloneContainerStructure = (
            source: ContainerType,
            parentName?: string,
            parentId?: string,
            isNested: boolean = false,
            nestedIndex?: number  // Index for nested container naming during duplication
        ): ContainerType => {
            const newId = generateComponentId(isNested ? 'nested-container' : 'container');

            let newName = source.name;
            if (parentName && nestedIndex !== undefined) {
                newName = `Container ${parentName}.${nestedIndex}`;
            } else if (parentName) {
                // For single nested container duplication, find siblings to calculate next number
                const parent = findContainer(layoutContainers, parentId || '');
                const siblings = parent?.content.nestedContainers || [];

                const siblingNumbers = siblings
                    .map(s => {
                        const match = s.name.match(/Container (.+)/);
                        if (match) {
                            const parts = match[1].split('.');
                            const lastPart = parts[parts.length - 1];
                            return parseInt(lastPart) || 0;
                        }
                        return 0;
                    });

                // Get max number and increment
                const maxNumber = siblingNumbers.length > 0 ? Math.max(...siblingNumbers) : 0;
                const newNumber = maxNumber + 1;

                // Build new name with parent prefix + new number
                const nameParts = source.name.match(/Container (.+)/);
                if (nameParts) {
                    const parts = nameParts[1].split('.');
                    parts[parts.length - 1] = newNumber.toString();
                    newName = `Container ${parts.join('.')}`;
                }
            } else {
                const containerNumbers = layoutContainers
                    .filter(c => !c.metadata?.isNested)
                    .map(c => {
                        const match = c.name.match(/Container (\d+)$/);
                        return match ? parseInt(match[1]) : 0;
                    });
                const maxNumber = containerNumbers.length > 0 ? Math.max(...containerNumbers) : 0;
                newName = `Container ${maxNumber + 1}`;
            }
            
            let clonedNestedContainers: ContainerType[] = [];
            if (source.content.nestedContainers && source.content.nestedContainers.length > 0) {
                clonedNestedContainers = source.content.nestedContainers.map((nested, index) => {
                    const parentNameForNested = newName.replace('Container ', '');
                    return cloneContainerStructure(
                        nested,
                        parentNameForNested,
                        newId,
                        true,
                        index + 1  // Pass index+1 to generate unique names (2.1, 2.2, 2.3, etc.)
                    );
                });
            }
            
            const clonedContainer: ContainerType = {
                id: newId,
                name: newName,
                order: source.order,
                configuration: {
                    ...source.configuration 
                },
                metadata: {
                    type: isNested ? 'nested-container' : 'container',
                    isNested: isNested,
                    parentContainerId: parentId,
                    rowIndex: source.metadata?.rowIndex || 0,
                    nestingLevel: source.metadata?.nestingLevel
                },
                content: {
                    type: 'widget',  
                    componentId: 'empty',  
                    'widget-type': 'contentBlock',
                    nestedContainers: clonedNestedContainers,  
                    items: [],  
                    submodel: undefined  
                },
                usageInfo: {
                    usedComponents: []  
                }
            };
            
            return clonedContainer;
        };

        const { container: containerToDuplicate, parentId, parentContainer } = findContainerWithParent(layoutContainers, containerId);

        if (!containerToDuplicate) {
            return;
        }

        const isNested = containerToDuplicate.metadata?.isNested === true;

        if (isNested && parentId) {
            const parentName = parentContainer?.name.replace('Container ', '');
            
            const clonedContainer = cloneContainerStructure(
                containerToDuplicate,
                parentName,
                parentId,
                true
            );
            
            const updatedContainers = updateContainersRecursively(layoutContainers, container => {
                if (container.id === parentId) {
                    const nestedContainers = container.content.nestedContainers || [];

                    const nextRowIndex = Math.max(
                        ...nestedContainers.map(c => c.metadata?.rowIndex || 0)
                    ) + 1;

                    clonedContainer.order = nestedContainers.length;
                    if (clonedContainer.metadata) {
                        clonedContainer.metadata.rowIndex = nextRowIndex;
                    }

                    const updatedNestedContainers = [...nestedContainers, clonedContainer];

                    return {
                        ...container,
                        content: {
                            ...container.content,
                            nestedContainers: updatedNestedContainers
                        }
                    };
                }

                return container;
            });
            setLayoutContainers(updatedContainers);
            updateActiveContainers(updatedContainers);

        } else {
            const clonedContainer = cloneContainerStructure(containerToDuplicate);
            
            const originalRow = containerRows[containerId] || 0;
            const newRow = originalRow + 1;
            
            const updatedContainerRows = { ...containerRows };
            Object.keys(updatedContainerRows).forEach(id => {
                if (updatedContainerRows[id] >= newRow) {
                    updatedContainerRows[id] += 1;
                }
            });
            
            updatedContainerRows[clonedContainer.id] = newRow;
            clonedContainer.metadata!.rowIndex = newRow;
            
            const originalIndex = layoutContainers.findIndex(c => c.id === containerId);
            const insertIndex = originalIndex + 1;
            
            const updatedContainers = [...layoutContainers];
            updatedContainers.forEach((container, index) => {
                if (index >= insertIndex) {
                    container.order += 1;
                }
            });
            
            clonedContainer.order = containerToDuplicate.order + 1;
            
            updatedContainers.splice(insertIndex, 0, clonedContainer);
            
            setContainerRows(updatedContainerRows);
            setLayoutContainers(updatedContainers);
            updateActiveContainers(updatedContainers);
        }
        
        markPageAsModified(editorState.activePageId);
  };

  const handleDeleteContainer = (containerId: string) => {
        if (!editorState.activePageId) return;
        
        const isDeletingSelectedContainer = editorState.selectedContainerId === containerId;
        
        if (isDeletingSelectedContainer) {
            setEditorState(prev => ({
                ...prev,
                selectedContainerId: null,
                selectedFieldId: null,
                selectedFieldType: null,
                selectedFieldParentId: null,
                selectedSubmodelId: null,
                selectionPath: []
            }));

            if (setSelectedContainerId) {
                setSelectedContainerId(null);
            }
            
            if (onContainerSelect) {
                onContainerSelect('');
            }
        }
        
        setTimeout(() => {
            deleteContainer(containerId);
            markPageAsModified(editorState?.activePageId ??'');
        }, 0);
  };

  if (!activePageId) {
      return <EmptyState message="Select or create a page to start building your UI" />;
  }

  if (!canvasMap[activePageId] && currentUIModelId) {
      return <EmptyState message="Loading canvas..." />;
  }

  if (!activeContainers || activeContainers.length === 0) {
      return (
        <EmptyState
          message="This page has no containers yet"
          actionLabel="Add Container"
          onAction={() => {
            const newContainer: ContainerType = {
              id: generateComponentId('container'),
              name: 'Container 1',
              order: 0,
              metadata: {
                type: 'container',
                isNested: false,
                rowIndex: 0,
              },
              configuration: {
                width: '100%',
                height: 'auto',
                alignment: 'left',
                layout: 'vertical',
                showTitle: true,
                widthLocked: false,
                collapsible: false
              },
              content: {
                type: 'widget',
                componentId: 'empty',
                'widget-type': 'contentBlock',
                nestedContainers: [],
                items: []
              },
              usageInfo: {
                usedComponents: []
              }
            };
            updateActiveContainers([newContainer]);
          }}
        />
      );
  }

  return (
    <div className="tw-flex-1 tw-overflow-auto tw-px-2">
      <div className="tw-pt-2">
        {rows.map((rowContainers, rowIndex) => (
          <React.Fragment key={`row-${rowIndex}`}>
            <div className="tw-flex tw-flex-row">
              {rowContainers.map((container, colIndex) => (
                <React.Fragment key={container.id}>
                  <Container
                    container={container}
                    isSelected={selectedContainerId === container.id}
                    onSelect={(id) => {
                      handleContainerSelect(id);
                    }}
                    onAddNestedHorizontal={(parentId) => {
                      const updatedContainers = handleAddNestedHorizontalContainer(parentId);
                      updateActiveContainers(updatedContainers);
                    }}
                    onAddNestedVertical={(parentId, rowIndex) => {
                      const updatedContainers = handleAddNestedVerticalContainer(parentId, rowIndex);
                      updateActiveContainers(updatedContainers);
                    }}
                    onAddNestedBetween={(parentId, beforeIndex) => {
                      const updatedContainers = handleAddNestedBetween(parentId, beforeIndex);
                      updateActiveContainers(updatedContainers);
                    }}
                    onRemoveComponent={handleRemoveComponent}
                    onFieldSelect={handleFieldSelect}
                    selectedFieldId={editorState.selectedFieldId}
                    onSubmodelSelect={onSubmodelSelect}
                    onWidgetSelect={onWidgetSelect}
                    onAddContainer={handleAddContainer}
                    onDuplicateContainer={handleDuplicateContainer}
                    onDeleteContainer={handleDeleteContainer}
                    onContentUpdate={handleContentUpdate}
                  />
                  
                  {colIndex < rowContainers.length - 1 || rowContainers.length < 3 ? (
                    <AddContainerLine
                      rowIndex={rowIndex}
                      orientation="vertical"
                      onAdd={(rowIndex) => {
                        const updatedContainers = handleAddVerticalContainer(rowIndex);
                        updateActiveContainers(updatedContainers);
                      }}
                    />
                  ) : null}
                </React.Fragment>
              ))}
            </div>
            
            <AddContainerLine
              rowIndex={rowIndex}
              orientation="horizontal"
              onAdd={(rowIndex) => {
                const updatedContainers = handleAddHorizontalContainer(rowIndex);
                updateActiveContainers(updatedContainers);
              }}
            />
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};